import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { useAuth } from '../../../contexts/AuthContext';

// Simple mock for workspace members if a real hook doesn't exist yet
const useWorkspaceMembers = () => {
    return [
        { uid: 'u1', displayName: 'Eleventh Admin', email: 'admin@oblique.com' },
        { uid: 'u2', displayName: 'Nat Gracia', email: 'nat@oblique.com' },
        { uid: 'u3', displayName: 'Alex Chen', email: 'alex@oblique.com' }
    ];
};

const PersonPicker = ({ single = false, multi = false, value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef(null);
    const { currentUser } = useAuth() || {};
    const members = useWorkspaceMembers();

    const isMulti = multi || !single;
    const valArray = Array.isArray(value) ? value : (value ? [value] : []);

    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [isOpen]);

    const togglePerson = (uid) => {
        if (isMulti) {
            const newVals = valArray.includes(uid)
                ? valArray.filter(id => id !== uid)
                : [...valArray, uid];
            onChange(newVals);
        } else {
            onChange(valArray.includes(uid) ? '' : uid);
            setIsOpen(false);
        }
    };

    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    };

    const renderSelectedChips = () => {
        if (valArray.length === 0) {
            return <span style={{ color: 'var(--text-tertiary)' }}>Select person...</span>;
        }

        return (
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                {valArray.map(uid => {
                    const person = members.find(m => m.uid === uid) || { displayName: uid };
                    const isMe = currentUser && currentUser.uid === uid;
                    return (
                        <div key={uid} style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            background: 'var(--bg-muted)',
                            border: '1px solid var(--border)',
                            color: 'var(--text-primary)',
                            padding: '2px 8px 2px 2px', borderRadius: '999px', fontSize: '11px', fontWeight: 500
                        }}>
                            <div style={{
                                width: '18px', height: '18px', borderRadius: '50%',
                                background: 'var(--accent)', color: 'white',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '8px', fontWeight: 'bold'
                            }}>
                                {isMe ? 'ME' : getInitials(person.displayName)}
                            </div>
                            {isMe ? 'Me' : person.displayName.split(' ')[0]}
                            {isMulti && (
                                <div 
                                    style={{ display: 'flex', cursor: 'pointer', opacity: 0.5, marginLeft: '2px' }} 
                                    onClick={(e) => { e.stopPropagation(); togglePerson(uid); }}
                                    className="hover-opacity-100 hover-color-danger"
                                >
                                    <ObliqueIcon name="close" size={12} />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    const filteredMembers = members.filter(m => 
        m.displayName?.toLowerCase().includes(search.toLowerCase()) || 
        m.email?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div ref={containerRef} style={{ position: 'relative', width: '100%', minWidth: '160px' }}>
            <div 
                onClick={() => setIsOpen(!isOpen)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                style={{
                    minHeight: '28px', width: '100%',
                    padding: '2px 8px',
                    border: `1px solid ${isOpen || isHovered ? 'var(--border-strong)' : 'var(--border)'}`,
                    borderRadius: '4px',
                    background: 'var(--bg-surface)',
                    display: 'flex', alignItems: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                    transition: 'border-color 0.15s ease-in-out',
                    boxSizing: 'border-box'
                }}
            >
                {renderSelectedChips()}
            </div>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 4, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 4, scale: 0.98 }}
                        transition={{ duration: 0.15 }}
                        style={{
                            position: 'absolute', top: 'calc(100% + 4px)', left: 0,
                            minWidth: '240px',
                            background: 'var(--bg-surface)', border: '1px solid var(--border)',
                            borderRadius: '8px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                            zIndex: 3000, padding: '8px',
                            display: 'flex', flexDirection: 'column', gap: '8px'
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', padding: '0 8px', background: 'var(--bg-subtle)', borderRadius: '6px', border: '1px solid var(--border)' }}>
                            <ObliqueIcon name="search" size={14} color="var(--text-tertiary)" />
                            <input
                                autoFocus
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search people..."
                                style={{
                                    border: 'none', background: 'transparent', outline: 'none',
                                    padding: '6px 8px', fontSize: '12px', width: '100%',
                                    color: 'var(--text-primary)'
                                }}
                            />
                        </div>

                        <div style={{ maxHeight: '200px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '2px' }} className="custom-scrollbar">
                            {currentUser && (!search || currentUser.displayName.toLowerCase().includes(search.toLowerCase())) && (
                                <div
                                    onClick={() => togglePerson(currentUser.uid)}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '10px',
                                        padding: '6px 8px', borderRadius: '6px', cursor: 'pointer',
                                        background: valArray.includes(currentUser.uid) ? 'var(--bg-muted)' : 'transparent'
                                    }}
                                    className="hover-bg-muted"
                                >
                                    <div style={{ width: '16px', color: 'var(--accent)', display: 'flex', justifyContent: 'center' }}>
                                        {valArray.includes(currentUser.uid) && <ObliqueIcon name="check" size={14} />}
                                    </div>
                                    <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: 'var(--accent)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 'bold' }}>
                                        ME
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>Me (current user)</span>
                                    </div>
                                </div>
                            )}
                            
                            {filteredMembers.filter(m => !currentUser || m.uid !== currentUser.uid).map(person => (
                                <div
                                    key={person.uid}
                                    onClick={() => togglePerson(person.uid)}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '10px',
                                        padding: '6px 8px', borderRadius: '6px', cursor: 'pointer',
                                        background: valArray.includes(person.uid) ? 'var(--bg-muted)' : 'transparent'
                                    }}
                                    className="hover-bg-muted"
                                >
                                    <div style={{ width: '16px', color: 'var(--accent)', display: 'flex', justifyContent: 'center' }}>
                                        {valArray.includes(person.uid) && <ObliqueIcon name="check" size={14} />}
                                    </div>
                                    <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: 'var(--bg-muted)', border: '1px solid var(--border)', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 'bold' }}>
                                        {getInitials(person.displayName)}
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>{person.displayName}</span>
                                        <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{person.email}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default PersonPicker;
